import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Users, Target, Lightbulb } from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: Target,
      title: "Find Your Sound",
      description: "Discover gear combinations that match your musical style and budget"
    },
    {
      icon: BookOpen,
      title: "Learn & Grow",
      description: "Educational content to help you understand tone, gear, and technique"
    },
    {
      icon: Users,
      title: "Master's Wisdom",
      description: "Study the setups of legendary guitarists and their iconic sounds"
    },
    {
      icon: Lightbulb,
      title: "Get Inspired",
      description: "Visual inspiration and technical insights to fuel your musical journey"
    }
  ];

  return (
    <section id="about" className="py-20 bg-charcoal/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-space-grotesk font-bold mb-6">
            <span className="gradient-text">About Pickarig</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Pickarig is your ultimate resource for guitar gear knowledge. Whether you're a beginner looking for your first setup or an experienced player seeking inspiration from the masters, we've got you covered.
          </p>
        </motion.div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-neon-green/20 to-electric-blue/20 rounded-xl mb-4"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <feature.icon className="h-8 w-8 text-neon-green" />
              </motion.div>
              <h3 className="text-xl font-space-grotesk font-semibold text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Technical insights section */}
        <motion.div 
          className="bg-dark-bg border border-charcoal rounded-xl p-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-space-grotesk font-bold text-white mb-4">
                Technical Insights
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Dive deep into the technical aspects of guitar tone. From understanding tube vs solid-state amps to choosing the right pickups for your style, we break down complex concepts into digestible insights.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-neon-green rounded-full"></div>
                  <span className="text-gray-300">Amp Technology Explained</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-electric-blue rounded-full"></div>
                  <span className="text-gray-300">Pickup Types & Characteristics</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-crimson rounded-full"></div>
                  <span className="text-gray-300">Effects Chain Optimization</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-neon-green/10 to-electric-blue/10 rounded-xl p-6 border border-charcoal">
                <div className="text-center">
                  <div className="text-4xl font-bold text-neon-green mb-2">500+</div>
                  <div className="text-gray-400">Gear Reviews</div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-electric-blue">50+</div>
                    <div className="text-gray-400 text-sm">Artist Profiles</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-crimson">100+</div>
                    <div className="text-gray-400 text-sm">Tone Guides</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
